import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import {
  AssessmentPagination,
  AssessmentsFilters,
  AssessmentsHeader,
  AssessmentSorting,
  AssessmentsTable,
  AssessmentsTableSkeleton,
  useGetAllAssessments,
} from '@features/assessment';
import { ProfileCompletionBanner } from '@features/tenant-profile-hub';
import { ASSESSMENTS_SORT_BY_TYPES, AssessmentStatus, LimitPerPage, Order, TimeFilter } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { AssessmentsSortByType, GetAllAssessmentsQueries } from '@libs/types';
import { useDebounce } from '@uidotdev/usehooks';

const assessmentValidators = {
  page: (value: string | null): number => {
    const num = value ? parseInt(value, 10) : 1;
    return Number.isInteger(num) && num >= 1 && num <= 999 ? num : 1;
  },

  limit: (value: string | null): LimitPerPage => {
    const num = value ? parseInt(value, 10) : LimitPerPage.TEN;
    return Object.values(LimitPerPage).includes(num as LimitPerPage)
      ? (num as LimitPerPage)
      : LimitPerPage.TEN;
  },

  status: (value: string | null): AssessmentStatus | null => {
    if (!value) return null;
    return Object.values(AssessmentStatus).includes(value as AssessmentStatus)
      ? (value as AssessmentStatus)
      : null;
  },

  timeFilter: (value: string | null): TimeFilter | null => {
    if (!value) return null;
    return Object.values(TimeFilter).includes(value as TimeFilter) ? (value as TimeFilter) : null;
  },

  sortBy: (value: string | null): AssessmentsSortByType => {
    const defaultSortBy: AssessmentsSortByType = 'updatedAt';
    if (!value) return defaultSortBy;
    return ASSESSMENTS_SORT_BY_TYPES.includes(value as AssessmentsSortByType)
      ? (value as AssessmentsSortByType)
      : defaultSortBy;
  },

  order: (value: string | null): Order => {
    const defaultOrder = Order.DESC;
    if (!value) return defaultOrder;
    return Object.values(Order).includes(value as Order) ? (value as Order) : defaultOrder;
  },
};

const sanitizeSearchParams = (
  searchParams: URLSearchParams,
): { sanitized: URLSearchParams; hasChanges: boolean } => {
  const sanitized = new URLSearchParams(searchParams);
  let hasChanges = false;

  // Validate and sanitize each parameter
  const page = assessmentValidators.page(searchParams.get('page'));
  if (searchParams.get('page') !== page.toString()) {
    sanitized.set('page', page.toString());
    hasChanges = true;
  }

  const limit = assessmentValidators.limit(searchParams.get('limit'));
  if (searchParams.get('limit') !== limit.toString()) {
    sanitized.set('limit', limit.toString());
    hasChanges = true;
  }

  const status = assessmentValidators.status(searchParams.get('status'));
  const statusParam = searchParams.get('status');
  if (statusParam !== (status || '')) {
    if (status) {
      sanitized.set('status', status);
    } else {
      sanitized.delete('status');
    }
    hasChanges = true;
  }

  const timeFilter = assessmentValidators.timeFilter(searchParams.get('timeFilter'));
  const timeFilterParam = searchParams.get('timeFilter');
  if (timeFilterParam !== (timeFilter || '')) {
    if (timeFilter) {
      sanitized.set('timeFilter', timeFilter);
    } else {
      sanitized.delete('timeFilter');
    }
    hasChanges = true;
  }

  const sortBy = assessmentValidators.sortBy(searchParams.get('sortBy'));
  if (searchParams.get('sortBy') !== sortBy) {
    sanitized.set('sortBy', sortBy);
    hasChanges = true;
  }

  const order = assessmentValidators.order(searchParams.get('order'));
  if (searchParams.get('order') !== order) {
    sanitized.set('order', order);
    hasChanges = true;
  }

  return { sanitized, hasChanges };
};

export const AssessmentsHome: FC = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const { sanitized, hasChanges } = sanitizeSearchParams(searchParams);
    if (hasChanges) {
      setSearchParams(sanitized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { page, limit, status, timeFilter, sortBy, order } = useMemo(() => {
    return {
      page: assessmentValidators.page(searchParams.get('page')),
      limit: assessmentValidators.limit(searchParams.get('limit')),
      status: assessmentValidators.status(searchParams.get('status')),
      timeFilter: assessmentValidators.timeFilter(searchParams.get('timeFilter')),
      sortBy: assessmentValidators.sortBy(searchParams.get('sortBy')),
      order: assessmentValidators.order(searchParams.get('order')),
    };
  }, [searchParams]);

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState(searchParams.get('searchBy') || '');
  const [statusFilter, setStatusFilter] = useState<AssessmentStatus | 'All Status'>(
    status || 'All Status',
  );

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Update URL when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchParams.get('searchBy')) {
      const newParams = new URLSearchParams(searchParams);
      if (debouncedSearchTerm) {
        newParams.set('searchBy', debouncedSearchTerm);
      } else {
        newParams.delete('searchBy');
      }
      setSearchParams(newParams);
    }
  }, [debouncedSearchTerm, searchParams, setSearchParams]);

  // Build API query parameters
  const apiParams: GetAllAssessmentsQueries = useMemo(() => {
    const params: GetAllAssessmentsQueries = {
      page,
      limit,
      sortBy,
      order,
    };

    const searchBy = searchParams.get('searchBy');
    if (searchBy) params.searchBy = searchBy;
    if (status) params.status = status;
    if (timeFilter) params.timeFilter = timeFilter;

    return params;
  }, [searchParams, status, timeFilter, page, limit, sortBy, order]);

  // Fetch assessments using React Query
  const { data: assessmentsData, isLoading, isError, refetch } = useGetAllAssessments(apiParams);

  // Sync local state with URL params when URL changes
  useEffect(() => {
    const urlSearchBy = searchParams.get('searchBy') || '';
    if (urlSearchBy !== searchTerm) {
      setSearchTerm(urlSearchBy);
    }
    setStatusFilter(status || 'All Status');
  }, [searchParams, status, timeFilter, sortBy, order]);

  // Update URL with new parameters
  const updateUrlParams = useCallback(
    (params: Partial<GetAllAssessmentsQueries>) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          newParams.set(key, value.toString());
        } else {
          newParams.delete(key);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Handle search change (debounced via useEffect)
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      updateUrlParams({
        page: 1,
      });
    },
    [updateUrlParams],
  );

  // Handle status filter change
  const handleStatusFilterChange = useCallback(
    (value: AssessmentStatus | 'All Status') => {
      setStatusFilter(value);
      updateUrlParams({
        status: value === 'All Status' ? undefined : value,
        page: 1,
      });
    },
    [updateUrlParams],
  );

  // Handle time filter change
  const handleTimeFilterChange = useCallback(
    (value: TimeFilter | 'All Time') => {
      updateUrlParams({
        timeFilter: value === 'All Time' ? undefined : value,
        page: 1,
      });
    },
    [updateUrlParams],
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: AssessmentsSortByType, newOrder: Order) => {
      updateUrlParams({ sortBy: newSortBy, order: newOrder });
    },
    [updateUrlParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('All Status');
    setSearchParams(new URLSearchParams({ page: '1', limit: limit.toString() }));
  }, [setSearchParams, limit]);

  const onNewAssessmentClick = () => {
    navigate(routes.assessmentNew());
  };

  // Handle pagination
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateUrlParams({ page: newPage });
    },
    [updateUrlParams],
  );

  // Get assessments and counts from API response
  const assessments = assessmentsData?.rows || [];
  const totalCount = assessmentsData?.meta.totalCount || 0;
  const currentCount = assessmentsData?.meta.currentCount || 0;
  const totalPages = assessmentsData?.meta.totalPages || 1;
  const hasNextPage = assessmentsData?.meta.hasNextPage || false;
  const hasPrevPage = assessmentsData?.meta.hasPrevPage || false;

  // Map time filter from API format to UI format
  const displayTimeFilter = useMemo((): TimeFilter | 'All Time' => {
    if (!timeFilter) return 'All Time';
    return timeFilter;
  }, [timeFilter]);

  // Show error state
  if (isError) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 4,
          p: 3,
        }}
      >
        <AssessmentsHeader onStartNewAssessmentClick={onNewAssessmentClick} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 2,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Failed to load assessments
            </Typography>
            <Typography variant="body2">
              There was an error loading your assessments. Please try again.
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderRadius: 4,
        p: 3,
      }}
    >
      <ProfileCompletionBanner />

      <AssessmentsHeader onStartNewAssessmentClick={onNewAssessmentClick} />

      <AssessmentsFilters
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        timeFilter={displayTimeFilter}
        onTimeFilterChange={handleTimeFilterChange}
        onClearFilters={handleClearFilters}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            <Trans
              i18nKey="app.assessments.showingResults"
              values={{
                showing: currentCount,
                total: totalCount,
              }}
            />
          </Typography>
        </Box>
        <AssessmentSorting sortBy={sortBy} order={order} onSortChange={handleSortChange} />
      </Box>

      {/* Loading State — table-shaped skeleton instead of a centered spinner.
          Matches the real table's column layout so swapping it out doesn't
          shift the page, and renders a flat bar for the progress column so
          there is no 0% → score animation when real data arrives. */}
      {isLoading ? (
        <AssessmentsTableSkeleton rowCount={limit} />
      ) : assessments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            py: 8,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              mb: 1,
            }}
          >
            <Trans i18nKey={'app.assessments.emptyState.noAssessments'} />
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.tertiary,
            }}
          >
            {searchParams.get('searchBy') || status || timeFilter ? (
              <Trans i18nKey={'app.assessments.emptyState.adjustFilters'} />
            ) : (
              <Trans i18nKey={'app.assessments.emptyState.getStarted'} />
            )}
          </Typography>
        </Box>
      ) : (
        <AssessmentsTable assessments={assessments} params={apiParams} />
      )}

      {/* Pagination Controls - Only show if there are assessments */}
      {!isLoading && assessments.length > 0 && (
        <AssessmentPagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          hasPrevPage={hasPrevPage}
          onPageChange={handlePageChange}
          isLoading={isLoading}
        />
      )}
    </Box>
  );
});
